import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  setReduxSelectedFile,
  setReduxSelectedParticipant,
} from "redux/appSlice";
import { FileField, RequestParticipant } from "models/interfaces";
import { MobileSignRequestStyles } from "../MobileSignRequest.styles";
import { pressableProps } from "../mobileA11y";
import DocumentViewer from "../../components/DocumentViewer";
import FieldsPanelMobile from "../../components/FieldsPanelMobile";
import MobileFieldOptionsBar from "../components/MobileFieldOptionsBar";
import MobileFieldTypeSheet from "../components/MobileFieldTypeSheet";
import MobileFieldFormattingSheet from "../components/MobileFieldFormattingSheet";
import MobileFieldDataSheet from "../components/MobileFieldDataSheet";
import MobileFieldMoreMenu from "../components/MobileFieldMoreMenu";
import MobileDocumentsSheet from "../components/MobileDocumentsSheet";
import MobileChooseSignerSheet from "../components/MobileChooseSignerSheet";
import MobileAiAssistantSheet from "../components/MobileAiAssistantSheet";

const getInitials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const CONTINUE_BAR_HEIGHT = 66; // fixed bottom Continue bar (approx)

const MobileSignRequestEditor: React.FC = () => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const { signRequest, selectedFile, selectedParticipant, isBulkSend } =
    useAppSelector((state: RootState) => state.app);

  const [docLoading, setDocLoading] = useState(true);
  const [selectedField, setSelectedField] = useState<string | null>(null);
  const [isFieldWithinBounds, setIsFieldWithinBounds] = useState(false);
  const [previewCoords, setPreviewCoords] = useState({ x: 0, y: 0 });
  const [openDocsSheet, setOpenDocsSheet] = useState(false);
  const [openSignerSheet, setOpenSignerSheet] = useState(false);
  const [openAiSheet, setOpenAiSheet] = useState(false);
  // Id of the placed field the user tapped. When set, the bottom field-type
  // palette is replaced by that field's options bar (Figma "Fields options").
  const [optionsFieldId, setOptionsFieldId] = useState<number | null>(null);
  // Whether the "Type" properties sheet is open for the selected field.
  const [typeSheetOpen, setTypeSheetOpen] = useState(false);
  // Whether the "Formatting" properties sheet is open for the selected field.
  const [formattingSheetOpen, setFormattingSheetOpen] = useState(false);
  // Whether the "Field Data" properties sheet is open for the selected field.
  const [fieldDataSheetOpen, setFieldDataSheetOpen] = useState(false);
  // Anchor for the "More option" popover (three-dots of the field options bar).
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  // Derive the live field from the store by id so the options bar / Type sheet
  // reflect edits and the bar auto-closes when the field is deleted.
  const optionsField: FileField | null =
    optionsFieldId != null
      ? (selectedFile?.fields?.find(
          (f: FileField) => f.id === optionsFieldId,
        ) ?? null)
      : null;
  const participants = (signRequest.signers || []).filter(
    (s: RequestParticipant) => !s.cc,
  );

  // Ensure a document and a participant are selected for the editor.
  useEffect(() => {
    // redux `selectedFile` starts as an empty placeholder object (truthy), so a
    // plain `!selectedFile` check never selects the real file and the viewer
    // hangs on "Loading document...". Select the first file whenever the current
    // selection doesn't match an actual file in the request.
    if (signRequest.files?.length) {
      const currentId = selectedFile?.unsignedDoc || selectedFile?.unsignedPdf;
      const hasValidSelection =
        !!currentId &&
        signRequest.files.some(
          (f: any) => (f.unsignedDoc || f.unsignedPdf) === currentId,
        );
      if (!hasValidSelection) {
        const first = signRequest.files[0] as any;
        dispatch(
          setReduxSelectedFile({
            ...first,
            unsignedDoc: first.unsignedDoc || first.unsignedPdf || "",
            unsignedDocUrl: first.unsignedDocUrl || first.unsignedPdfUrl || "",
          }),
        );
      }
    }
    if (!selectedParticipant && (participants.length || signRequest.signers?.length)) {
      dispatch(
        setReduxSelectedParticipant(participants[0] || signRequest.signers[0]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signRequest, selectedFile, selectedParticipant, dispatch]);


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: m.surface.white,
      }}
    >
      {/* Placing for bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: m.grey[400],
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Placing for
          </Typography>

          {selectedParticipant && (
            <Box
              {...(isBulkSend
                ? {}
                : pressableProps(() => setOpenSignerSheet(true), {
                    label: `Placing fields for ${
                      selectedParticipant.name || "signer"
                    }. Change signer`,
                    hasPopup: "dialog",
                  }))}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                cursor: isBulkSend ? "default" : "pointer",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: m.base[300],
                  color: m.surface.white,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {getInitials(selectedParticipant.name)}
              </Box>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: m.base[900],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 110,
                }}
              >
                {selectedParticipant.name || "Select signer"}
              </Typography>
              {!isBulkSend && (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 18, color: m.base[900], flexShrink: 0 }}
                />
              )}
            </Box>
          )}

        </Box>

        {/* Documents switcher */}
        <Box
          {...pressableProps(() => setOpenDocsSheet(true), {
            label: "Switch document",
            hasPopup: "dialog",
          })}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: m.ePurple[500],
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
            Documents
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {/* Document viewer (reused engine) on a gray canvas (mobile only) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: m.grey[50],
          py: 1.5,
          px: 1.5,
          // Center the PDF pages on the gray canvas. The page boxes are
          // inline-block inside react-pdf's Document wrapper, so center there.
          // Keep it scrollable but hide the scrollbar on mobile.
          "& #pdf_box": {
            alignItems: "center",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none !important",
              width: "0 !important",
            },
          },
          "& .react-pdf__Document": {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          },
        }}
      >
        <DocumentViewer
          docLoading={docLoading}
          setDocLoading={setDocLoading}
          isFieldWithinBounds={isFieldWithinBounds}
          previewCoords={previewCoords}
          selectedPreviewField={selectedField}
          setSelectedPreviewField={setSelectedField}
          onFieldSelect={(f) => {
            setOptionsFieldId(f ? f.id : null);
            if (!f) {
              setTypeSheetOpen(false);
              setFormattingSheetOpen(false);
              setFieldDataSheetOpen(false);
            }
          }}
          isMobile
        />
      </Box>

      {/* Bottom toolbar: the field's options bar when a placed field is
          selected, otherwise the field-type palette. The palette stays mounted
          (just hidden) so its one-time setup effects don't re-run on each swap. */}
      <Box sx={{ display: optionsField ? "none" : "block", flexShrink: 0 }}>
        <FieldsPanelMobile
          chipVariant="labeled"
          setIsFieldWithinBounds={setIsFieldWithinBounds}
          setPreviewCoords={setPreviewCoords}
          selectedField={selectedField}
          setSelectedField={setSelectedField}
          isMobile
          containerSx={{ position: "static", px: 1.5, flexShrink: 0 }}
        />
      </Box>
      {optionsField && (
        <MobileFieldOptionsBar
          field={optionsField}
          activeOption={
            typeSheetOpen
              ? "type"
              : formattingSheetOpen
                ? "formatting"
                : fieldDataSheetOpen
                  ? "fieldData"
                  : null
          }
          onOptionClick={(option) => {
            if (option === "type") {
              setFormattingSheetOpen(false);
              setFieldDataSheetOpen(false);
              setTypeSheetOpen(true);
            } else if (option === "formatting") {
              setTypeSheetOpen(false);
              setFieldDataSheetOpen(false);
              setFormattingSheetOpen(true);
            } else if (option === "fieldData") {
              setTypeSheetOpen(false);
              setFormattingSheetOpen(false);
              setFieldDataSheetOpen(true);
            }
          }}
          onMoreClick={(anchor) => setMoreAnchor(anchor)}
          containerSx={{ px: 1.5, flexShrink: 0 }}
        />
      )}

      <MobileFieldTypeSheet
        open={typeSheetOpen && !!optionsField}
        onClose={() => setTypeSheetOpen(false)}
        field={optionsField}
      />

      <MobileFieldFormattingSheet
        open={formattingSheetOpen && !!optionsField}
        onClose={() => setFormattingSheetOpen(false)}
        field={optionsField}
      />

      <MobileFieldDataSheet
        open={fieldDataSheetOpen && !!optionsField}
        onClose={() => setFieldDataSheetOpen(false)}
        field={optionsField}
      />

      <MobileFieldMoreMenu
        anchorEl={moreAnchor}
        field={optionsField}
        onClose={() => setMoreAnchor(null)}
      />

      {/* AI assistant FAB — hidden while the AI sheet is open so it doesn't
          show over/on top of the drawer. */}
      {!openAiSheet && (
        <Box
          {...pressableProps(() => setOpenAiSheet(true), {
            label: "Open Inkfree AI assistant",
            hasPopup: "dialog",
          })}
          sx={{
            position: "fixed",
            right: 16,
            bottom: CONTINUE_BAR_HEIGHT + 90,
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: classes.primaryGradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 6px 16px rgba(220,57,19,0.35)",
            // Above placed fields (which use zIndex 20/30) so no field is ever
            // shown over the circle, but below MUI sheets/drawers (~1200).
            zIndex: 1000,
            cursor: "pointer",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: m.surface.white }} />
        </Box>
      )}

      <MobileDocumentsSheet
        open={openDocsSheet}
        onClose={() => setOpenDocsSheet(false)}
      />

      <MobileChooseSignerSheet
        open={openSignerSheet}
        onClose={() => setOpenSignerSheet(false)}
      />

      {/* In the embedded editor its calls go to the draft's embed endpoints. */}
      <MobileAiAssistantSheet
        open={openAiSheet}
        onClose={() => setOpenAiSheet(false)}
      />
    </Box>
  );
};

export default MobileSignRequestEditor;
